import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import queryString from "query-string";

import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";
import { subCategoryFilter } from "app/filters/ngFilters";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type {
    ApiTrafficSourceItem,
    ApiTrafficSourceResponse,
    FilterParams,
    ITrafficSourceDataItem,
    ITrafficSourceResponse,
    QueryParams,
    SortConfig,
} from "./types";

const API_ENDPOINT = "/widgetApi/MarketingMixTotal/TrafficSourcesOverviewBeta/Table";
const EXCEL_ENDPOINT = "/widgetApi/MarketingMixTotal/TrafficSourcesOverviewBeta/Data/Excel";
const STALE_TIME = 5 * 60 * 1000;

const fetchService = DefaultFetchService.getInstance();

const createUrl = (domain: string): string =>
    proNavigator.href("digitalsuite_website_websiteperformance", {
        ...proNavigator.getParams(),
        key: domain,
    });

const transformSiteOrigins = (item: ApiTrafficSourceItem): Record<string, number> => {
    if (item.SiteOrigins && Object.keys(item.SiteOrigins).length > 0) {
        return item.SiteOrigins;
    }
    if (!item.site_split?.length || !item.share_split?.length) {
        return {};
    }
    return item.site_split.reduce(
        (acc, site, index) => {
            acc[site] = item.share_split?.[index] || 0;
            return acc;
        },
        {} as Record<string, number>,
    );
};

const transformItem = (item: ApiTrafficSourceItem, isCompare: boolean): ITrafficSourceDataItem => {
    const children = item.Children?.map((child) => transformItem(child, isCompare)) || [];

    const baseItem: ITrafficSourceDataItem = {
        domain: item.Domain,
        sourceType: item.SourceType,
        change: item.Change,
        share: item.Share,
        category: subCategoryFilter()(item.Category),
        rank: item.Rank,
        totalShare: isCompare ? item.FilteredShare || item.Share || 0 : item.TotalShare,
        siteOrigins: isCompare ? transformSiteOrigins(item) : item.SiteOrigins,
        icon: item.Favicon,
        url: createUrl(item.Domain),
    };

    if (children.length > 0) {
        baseItem.children = children;
    }

    return baseItem;
};

const fetchTrafficSourcesData = async (params: QueryParams): Promise<ITrafficSourceResponse> => {
    const response = await fetchService.get<ApiTrafficSourceResponse>(API_ENDPOINT, params);
    const isCompare = isCompareMode();

    return {
        totalCount: response.TotalCount,
        categories: response.Filters.category,
        sourceTypes: response.Filters.sourceType,
        items: response.Data.map((item) => transformItem(item, isCompare)),
    };
};

const buildFilters = (opts: { search?: string; category?: string | null; sourceType?: string | null }): string => {
    const parts: string[] = [];

    if (opts.sourceType) {
        parts.push(`sourceType;==;"${opts.sourceType}"`);
    }
    if (opts.search?.trim()) {
        parts.push(`Domain;contains;"${opts.search.trim()}"`);
    }
    if (opts.category) {
        parts.push(`Category;category;"${opts.category}"`);
    }

    return parts.join(",");
};

export const useTrafficSourcesData = (filterParams: FilterParams) => {
    const { params } = useCurrentStateAndParams();
    const { webSource, isWWW, duration, comparedDuration, key, country, sourceType, ignoreFilterConsistency } = params;

    const { from, to, compareFrom, compareTo, isWindow } = DurationService.getDurationData(
        duration,
        comparedDuration,
    ).forAPI;

    const filterString = useMemo(
        () =>
            buildFilters({
                search: filterParams?.searchTerm ?? "",
                category: filterParams?.selectedCategoryId ?? null,
                sourceType: filterParams?.selectedSourceId ?? null,
            }),
        [filterParams?.searchTerm, filterParams?.selectedCategoryId, filterParams?.selectedSourceId],
    );

    const orderBy = useMemo(() => {
        const { sortConfig } = filterParams || {};
        if (!sortConfig) {
            return undefined;
        }

        const fieldMapping = {
            domain: "Domain",
            share: "Share",
            change: "Change",
            sourceType: "SourceType",
            rank: "Rank",
            category: "Category",
        } as const;

        const { key, ascending } = sortConfig;
        const apiField = fieldMapping[key as keyof typeof fieldMapping] || "Share";
        const direction = ascending ? "asc" : "desc";

        return `${apiField} ${direction}`;
    }, [filterParams?.sortConfig]);

    const queryParams: QueryParams = {
        country,
        to,
        from,
        compareFrom,
        compareTo,
        isWindow,
        webSource,
        sourceType,
        ignoreFilterConsistency,
        timeGranularity: duration === "28d" ? "Daily" : "Monthly",
        includeSubDomains: isWWW === "*",
        keys: key,
        filter: filterString,
        page: filterParams?.currentPage ?? 1,
        orderBy,
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: ["trafficSourcesData", queryParams],
        queryFn: () => fetchTrafficSourcesData(queryParams),
        staleTime: STALE_TIME,
        enabled: !!key,
    });

    const tableData = data?.items || [];
    const totalCount = data?.totalCount || 0;
    const categories = data?.categories || [];
    const sourceTypes = data?.sourceTypes || [];

    return {
        data: tableData,
        totalCount,
        categories,
        sourceTypes,
        isLoading,
        isError,
    };
};

export const useTableState = () => {
    const { params } = useCurrentStateAndParams();
    const isCompare = isCompareMode();

    const COMPARE_MODE_COLUMNS = ["share", "siteOrigins", "sourceType", "category"];
    const SINGLE_MODE_COLUMNS = ["share", "change", "sourceType", "rank", "category"];

    const initialSortConfig: SortConfig = { key: "share", ascending: false };
    const initialVisibleColumns = new Set(isCompare ? COMPARE_MODE_COLUMNS : SINGLE_MODE_COLUMNS);
    const initialCategoryId =
        params.tableCategory && params.tableCategory !== "no-category" ? params.tableCategory : null;
    const initialSourceId = params.sourceType || null;

    return {
        initialSortConfig,
        initialVisibleColumns,
        initialCategoryId,
        initialSourceId,
    };
};

export const useExcelDownloadUrl = (filterParams: FilterParams): string => {
    const { params } = useCurrentStateAndParams();
    const { webSource, isWWW, duration, comparedDuration, key, country, sourceType, ignoreFilterConsistency } = params;

    const { from, to, compareFrom, compareTo, isWindow } = DurationService.getDurationData(
        duration,
        comparedDuration,
    ).forAPI;

    const filterString = useMemo(
        () =>
            buildFilters({
                search: filterParams?.searchTerm ?? "",
                category: filterParams?.selectedCategoryId ?? null,
                sourceType: filterParams?.selectedSourceId ?? null,
            }),
        [filterParams?.searchTerm, filterParams?.selectedCategoryId, filterParams?.selectedSourceId],
    );

    const orderBy = useMemo(() => {
        const { sortConfig } = filterParams || {};
        if (!sortConfig) {
            return undefined;
        }

        const fieldMapping = {
            domain: "Domain",
            share: "Share",
            change: "Change",
            sourceType: "SourceType",
            rank: "Rank",
            category: "Category",
        } as const;

        const { key, ascending } = sortConfig;
        const apiField = fieldMapping[key as keyof typeof fieldMapping] || "Share";
        const direction = ascending ? "asc" : "desc";

        return `${apiField} ${direction}`;
    }, [filterParams?.sortConfig]);

    const excelParams = useMemo(() => {
        const params: Record<string, unknown> = {
            country,
            to,
            from,
            compareFrom,
            compareTo,
            isWindow,
            webSource,
            sourceType,
            timeGranularity: duration === "28d" ? "Daily" : "Monthly",
            includeSubDomains: isWWW === "*",
            keys: key,
            filter: filterString,
        };

        if (orderBy) {
            params.orderBy = orderBy;
        }

        return params;
    }, [
        country,
        to,
        from,
        compareFrom,
        compareTo,
        isWindow,
        webSource,
        sourceType,
        duration,
        isWWW,
        key,
        filterString,
        orderBy,
    ]);

    return useMemo(() => {
        if (!key) {
            return "";
        }
        return `${EXCEL_ENDPOINT}?${queryString.stringify(excelParams)}`;
    }, [key, excelParams]);
};
